import React, { useState } from 'react';
import { useAgent } from '../../context/AgentContext';
import ChatInterface from '../chat/ChatInterface';
import DocumentUpload from '../documents/DocumentUpload';
import DocumentList from '../documents/DocumentList';
import { MessageSquare, FileText, Settings } from 'lucide-react';
import './AgentWorkspace.css';

const AgentWorkspace = () => {
  const { selectedAgent, activeConversation, createConversation } = useAgent();
  const [activeTab, setActiveTab] = useState('chat');

  const handleStartChat = async () => {
    await createConversation(selectedAgent._id);
    setActiveTab('chat');
  };

  const tabs = [
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="agent-workspace">
      <div className="workspace-header">
        <div className="workspace-agent-info">
          <div
            className="workspace-agent-icon"
            style={{ backgroundColor: selectedAgent.color || '#3B82F6' }}
          >
            {selectedAgent.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2>{selectedAgent.name}</h2>
            <span className="workspace-agent-type">{selectedAgent.type} agent</span>
          </div>
        </div>

        <div className="workspace-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`workspace-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="workspace-content">
        {activeTab === 'chat' && (
          <div className="workspace-chat">
            {activeConversation ? (
              <ChatInterface />
            ) : (
              <div className="workspace-empty">
                <MessageSquare size={48} className="empty-icon" />
                <h3>Start a Conversation</h3>
                <p>Ask questions about your uploaded documents, generate summaries, or extract insights.</p>
                <button className="btn btn-primary" onClick={handleStartChat}>
                  <MessageSquare size={18} />
                  New Conversation
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="workspace-documents">
            <div className="documents-header">
              <h3>Documents</h3>
              <p>Upload and manage documents for this agent's knowledge base</p>
            </div>
            <DocumentUpload />
            <DocumentList />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="workspace-settings">
            <div className="settings-section">
              <h3>Agent Settings</h3>
              <div className="settings-form">
                <div className="input-group">
                  <label>Agent Name</label>
                  <input
                    type="text"
                    className="input"
                    value={selectedAgent.name}
                    readOnly
                  />
                </div>
                <div className="input-group">
                  <label>Agent Type</label>
                  <input
                    type="text"
                    className="input"
                    value={selectedAgent.type}
                    readOnly
                  />
                </div>
                <div className="input-group">
                  <label>Description</label>
                  <textarea
                    className="input"
                    value={selectedAgent.description || 'No description'}
                    readOnly
                    rows={3}
                  />
                </div>
                <div className="input-group">
                  <label>System Prompt</label>
                  <textarea
                    className="input"
                    value={selectedAgent.systemPrompt}
                    readOnly
                    rows={5}
                  />
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>AI Settings</h3>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Temperature</label>
                  <span>{selectedAgent.settings?.temperature || 0.7}</span>
                </div>
                <div className="setting-item">
                  <label>Max Tokens</label>
                  <span>{selectedAgent.settings?.maxTokens || 1000}</span>
                </div>
                <div className="setting-item">
                  <label>Context Window</label>
                  <span>{selectedAgent.settings?.contextWindow || 5} chunks</span>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Statistics</h3>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Documents</label>
                  <span>{selectedAgent.stats?.documentCount || 0}</span>
                </div>
                <div className="setting-item">
                  <label>Total Queries</label>
                  <span>{selectedAgent.stats?.queryCount || 0}</span>
                </div>
                <div className="setting-item">
                  <label>Created</label>
                  <span>{new Date(selectedAgent.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentWorkspace;
